import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  BooleanInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  Toolbar,
  SaveButton,
  useTranslate,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ServiceProviderField } from '../ServiceProviderField';
import { MaintenanceFileUpload } from '../MaintenanceFileUpload';

const MaintenanceToolbar = () => (
  <Toolbar sx={{ 
    display: 'flex', 
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  }}>
    <SaveButton />
  </Toolbar>
);

interface MaintenanceDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const MaintenanceDialog: React.FC<MaintenanceDialogProps> = ({ 
  open, 
  onClose, 
  equipmentId,
  recordId,
  isEdit = false
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [maintenanceFiles, setMaintenanceFiles] = useState<any[]>([]);

  const serviceTypes = [
    { id: 'OIL_CHANGE', name: 'Oil Change' },
    { id: 'INSPECTION', name: 'Inspection' },
    { id: 'REPAIR', name: 'Repair' },
    { id: 'TIRE_SERVICE', name: 'Tire Service' },
    { id: 'BRAKE_SERVICE', name: 'Brake Service' },
    { id: 'ENGINE_SERVICE', name: 'Engine Service' },
    { id: 'TRANSMISSION_SERVICE', name: 'Transmission Service' },
    { id: 'ELECTRICAL', name: 'Electrical' },
    { id: 'BODYWORK', name: 'Bodywork' },
    { id: 'PREVENTIVE', name: 'Preventive Maintenance' },
    { id: 'EMERGENCY', name: 'Emergency Repair' },
    { id: 'OTHER', name: 'Other' }
  ];

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      setLoading(true);
      dataProvider.getOne('equipment-maintenance', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch((error) => {
          console.error('Error loading maintenance record:', error);
          notify('Error loading maintenance record', { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!isEdit) {
      setRecord(null);
    }
    // Reset files when dialog opens/closes
    if (!open) {
      setMaintenanceFiles([]);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
        files: maintenanceFiles,
      };
      
      console.log('Submitting maintenance data:', formattedData);
      
      if (isEdit && recordId) {
        await dataProvider.update('equipment-maintenance', {
          id: recordId,
          data: formattedData,
          previousData: record || {}
        });
        notify('Maintenance record updated successfully', { type: 'success' });
      } else {
        // Use fetch directly to call the correct endpoint
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        };
        
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        
        // Add tenant code header
        const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
        if (tenantsEnabled) {
          const currentTenantJson = localStorage.getItem('currentTenant');
          if (currentTenantJson) {
            try {
              const currentTenant = JSON.parse(currentTenantJson);
              if (currentTenant && currentTenant.code) {
                headers['x-tenant-code'] = currentTenant.code;
              }
            } catch (e) {
              console.error('Error parsing tenant:', e);
            }
          }
        } else {
          headers['x-tenant-code'] = 'default';
        }
        
        const response = await fetch(`${baseUrl}/equipment/${equipmentId}/maintenance`, {
          method: 'POST',
          headers,
          body: JSON.stringify(formattedData),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to create maintenance record: ${response.statusText}`);
        }
        
        notify('Maintenance record created successfully', { type: 'success' });
      }
      
      refresh();
      onClose();
    } catch (error) {
      console.error('Maintenance submission error:', error);
      notify(`Error ${isEdit ? 'updating' : 'creating'} maintenance record: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        {isEdit ? 'Edit Maintenance Record' : 'Add Maintenance Record'}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm 
          onSubmit={handleSubmit} 
          record={record || { 
            serviceDate: new Date().toISOString().split('T')[0] 
          }}
          toolbar={<MaintenanceToolbar />}
          sx={{
            '& .RaSimpleForm-input': { width: '100%', maxWidth: '100%' },
            '& .MuiFormControl-root': { marginBottom: 0, width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <ServiceProviderField source="serviceProviderId" label="Service Provider" />
            <DateInput source="serviceDate" validate={[required()]} label="Service Date" variant="outlined" size="small" fullWidth />
            <SelectInput source="serviceType" choices={serviceTypes} validate={[required()]} label="Service Type" variant="outlined" size="small" fullWidth />
            <NumberInput source="serviceMileage" label="Service Mileage" variant="outlined" size="small" fullWidth />
            <NumberInput source="totalCost" label="Total Cost ($)" variant="outlined" size="small" fullWidth />
            <TextInput source="description" multiline rows={3} label="Service Description" variant="outlined" size="small" fullWidth />
            {!isEdit && (
              <MaintenanceFileUpload files={maintenanceFiles} onFilesChange={setMaintenanceFiles} />
            )}
          </Box>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};